import { Link } from 'react-router-dom';
import {
  ArrowRightIcon,
  BanknotesIcon,
  CheckCircleIcon,
  ShieldCheckIcon,
  SparklesIcon,
  TruckIcon,
} from '@heroicons/react/24/outline';
import type { RefObject } from 'react';
import { HomeStaticImage } from '../HomeStaticImage';
import { homeCta } from '../homeCtaStyles';
import {
  HERO_MOBILE_IMAGES,
  HERO_PIN_COL1,
  HERO_PIN_COL2,
  HERO_PIN_COL3,
  loopHeroPinterestTrack,
} from '../heroPinterestImages';

type HomeHeroProps = Readonly<{
  heroRef: RefObject<HTMLElement | null>;
}>;

function HeroColumn({
  images,
  trackClass,
  colKey,
}: Readonly<{
  images: readonly { src: string; h: string }[];
  trackClass: string;
  colKey: string;
}>) {
  const looped = loopHeroPinterestTrack(images);
  return (
    <div className="flex flex-col gap-4 w-[32%]">
      <div className="relative overflow-hidden rounded-3xl h-[600px]">
        <div className={`home-pinterest-track ${trackClass} flex flex-col gap-4`}>
          {looped.map((img, i) => (
            <div key={`${colKey}-${img.src}-${i}`} className={`${img.h} shrink-0 rounded-3xl overflow-hidden`}>
              <HomeStaticImage
                src={img.src}
                alt={`Qumashi design ${(i % images.length) + 1}`}
                priority={colKey === 'col1' && i === 0}
                profile="hero"
                className="h-full w-full object-cover"
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function HomeHero({ heroRef }: HomeHeroProps) {
  return (
    <section ref={heroRef} className="home-hero relative min-h-screen flex flex-col overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
        <div className="home-hero-orb home-hero-orb--tl" />
        <div className="home-hero-orb home-hero-orb--tr" />
        <div className="home-hero-orb home-hero-orb--bl" />
        <div className="home-hero-orb home-hero-orb--center" />
      </div>

      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 z-[5] h-32 sm:h-40 md:h-48 lg:h-56"
        style={{
          background:
            'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.22) 32%, rgba(255,255,255,0.72) 62%, rgba(255,255,255,0.96) 85%, #ffffff 100%)',
        }}
        aria-hidden
      />

      <div className="relative z-10 flex-1 flex items-center max-w-6xl xl:max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-12 pt-16 md:pt-20">
        <div className="w-full grid grid-cols-1 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] gap-10 lg:gap-14 items-center">
          <div className="text-center lg:text-left relative z-10">
            <div className="home-anim-fade-up mb-5">
              <div className="home-glass-pill inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-purple-700 text-xs font-medium shadow-sm">
                <SparklesIcon className="h-3.5 w-3.5 text-purple-600" />
                <span>Moroccan streetwear tees &amp; hoodies</span>
              </div>
            </div>

            <h1 className="home-anim-fade-up home-anim-delay-1 text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tight leading-tight">
              <span className="block text-slate-900">Wear what</span>
              <span className="home-gradient-text block bg-gradient-to-r from-purple-400 via-indigo-400 to-purple-400 text-transparent bg-clip-text">
                no one else has.
              </span>
            </h1>

            <p className="home-anim-fade-up home-anim-delay-2 mt-4 text-base md:text-lg text-slate-600 max-w-xl mx-auto lg:mx-0">
              Shop Moroccan-made streetwear tees, hoodies, and creator drops—premium fit,
              premium print. Not mass merch.
            </p>

            <div className="home-anim-fade-in home-anim-delay-3 mt-4 flex flex-wrap items-center gap-2 text-sm text-slate-500 justify-center lg:justify-start">
              <span className="home-glass-chip inline-flex items-center gap-2 rounded-full px-3 py-1 shadow-sm">
                <ShieldCheckIcon className="h-4 w-4 text-emerald-600" />
                Secure checkout
              </span>
              <span className="home-glass-chip inline-flex items-center gap-2 rounded-full px-3 py-1 shadow-sm">
                <BanknotesIcon className="h-4 w-4 text-amber-600" />
                Cash on delivery in Morocco
              </span>
              <span className="home-glass-chip inline-flex items-center gap-2 rounded-full px-3 py-1 shadow-sm">
                <TruckIcon className="h-4 w-4 text-indigo-600" />
                3–7 day delivery in Morocco
              </span>
              <span className="home-glass-chip inline-flex items-center gap-2 rounded-full px-3 py-1 shadow-sm">
                <CheckCircleIcon className="h-4 w-4 text-purple-600" />
                30‑day returns
              </span>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
              <Link to="/shop" className={homeCta.primaryCompact}>
                Shop Streetwear Drops
              </Link>
              <Link
                to="/design-tool"
                className="inline-flex items-center justify-center px-6 py-3 rounded-full border border-slate-200 bg-white text-slate-900 font-semibold hover:bg-purple-50/60 transition-all"
              >
                Create Your Own
              </Link>
            </div>

          </div>

          <div className="home-anim-fade-in-right home-anim-delay-1 hidden md:flex justify-center relative z-10">
            <div
              className="home-hero-grid-panel flex gap-4 rounded-[32px] p-3 md:p-4 w-full max-w-md lg:max-w-lg"
              style={{ boxShadow: '0 30px 60px rgba(0, 0, 0, 0.08)' }}
            >
              <HeroColumn images={HERO_PIN_COL1} trackClass="home-pinterest-track--down" colKey="col1" />
              <HeroColumn images={HERO_PIN_COL2} trackClass="home-pinterest-track--up" colKey="col2" />
              <HeroColumn
                images={HERO_PIN_COL3}
                trackClass="home-pinterest-track--down-long"
                colKey="col3"
              />
            </div>
          </div>

          <div className="home-anim-fade-up home-anim-delay-4 md:hidden mt-8 relative z-10">
            <div className="home-hero-grid-panel flex gap-3 rounded-[32px] p-3 shadow-lg">
              <div className="grid grid-cols-2 gap-3 w-full">
                {HERO_MOBILE_IMAGES.map((img, i) => (
                  <div key={`mobile-${img.src}`} className={`${img.h} rounded-3xl overflow-hidden`}>
                    <HomeStaticImage
                      src={img.src}
                      alt={`Qumashi design ${i + 1}`}
                      priority={i === 0}
                      profile="hero"
                      className="h-full w-full object-cover"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
